import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { 
  formatDate, 
  formatTime,
  formatCurrency,
  getStatusBadgeClass 
} from '../../utils/formatters';
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus,
  Calendar as CalendarIcon,
  MapPin,
  Clock,
  DollarSign,
  User,
  X
} from 'lucide-react';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  addDays, 
  addMonths, 
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
  parseISO
} from 'date-fns';

// Helper to compare dates ignoring timezone issues
// Extracts YYYY-MM-DD from ISO string and compares with local date
function isSameDateString(isoDateString, localDate) {
  const dateFromApi = isoDateString.split('T')[0]; // "2026-01-10"
  const localDateStr = format(localDate, 'yyyy-MM-dd'); // "2026-01-10"
  return dateFromApi === localDateStr;
}

// Helper to get initials from a name
function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// Avatar component for contractor initials
function ContractorAvatar({ name, photo, size = 'sm' }) {
  const initials = getInitials(name);
  const sizeClasses = size === 'sm' ? 'w-5 h-5 text-[10px]' : 'w-6 h-6 text-xs';
  
  if (photo) {
    return (
      <img 
        src={`${import.meta.env.VITE_API_URL}/uploads/${photo}`}
        alt={name}
        className={`${sizeClasses} rounded-full object-cover`}
        title={name}
      />
    );
  }
  
  return (
    <div 
      className={`${sizeClasses} rounded-full bg-hub-600 text-white flex items-center justify-center font-medium`}
      title={name}
    >
      {initials}
    </div>
  );
}

// Stacked avatars for multiple contractors
function ContractorAvatars({ assignments, max = 3 }) {
  if (!assignments || assignments.length === 0) return null;
  
  const shown = assignments.slice(0, max);
  const overflow = assignments.length - max;
  
  return (
    <div className="flex -space-x-1">
      {shown.map((a, i) => (
        <div key={a.id || i} className="ring-1 ring-white rounded-full">
          <ContractorAvatar 
            name={a.contractor?.name} 
            photo={a.contractor?.profilePhoto}
            size="sm" 
          />
        </div>
      ))}
      {overflow > 0 && (
        <div 
          className="w-5 h-5 rounded-full bg-gray-400 text-white flex items-center justify-center text-[10px] font-medium ring-1 ring-white"
          title={`+${overflow} more`}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}

// Get background color for gig based on client color or status
function getGigColor(gig) {
  // CANCELLED gigs get solid red with white text
  if (gig.status === 'CANCELLED') {
    return { backgroundColor: '#DC2626', textColor: 'text-white', isDraft: false, isCancelled: true };
  }
  
  // DRAFT gigs get no fill - they'll have yellow outline instead
  if (gig.status === 'DRAFT') {
    return { backgroundColor: 'transparent', textColor: 'text-gray-700', isDraft: true, isCancelled: false };
  }
  
  // Priority: client color > status color
  const clientColor = gig.location?.venue?.client?.color;
  if (clientColor) {
    return { backgroundColor: clientColor, textColor: 'text-gray-800', isDraft: false, isCancelled: false };
  }
  
  // Fallback to status-based colors
  switch (gig.status) {
    case 'PENDING':
      return { backgroundColor: '#FEF3C7', textColor: 'text-yellow-800', isDraft: false, isCancelled: false };
    case 'CONFIRMED':
      return { backgroundColor: '#D1FAE5', textColor: 'text-green-800', isDraft: false, isCancelled: false };
    case 'COMPLETED':
      return { backgroundColor: '#F3F4F6', textColor: 'text-gray-600', isDraft: false, isCancelled: false };
    default:
      return { backgroundColor: '#F3F4F6', textColor: 'text-gray-600', isDraft: false, isCancelled: false };
  }
}

// Helper to calculate smart popup position within viewport bounds
function calculatePopupPosition(triggerRect, popupWidth = 280, popupHeight = 350) {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const padding = 8; // Min distance from viewport edges
  
  // Start with position below the trigger, centered
  let top = triggerRect.bottom + 4;
  let left = triggerRect.left + (triggerRect.width / 2) - (popupWidth / 2);
  
  // Check if popup goes below viewport - flip to above
  if (top + popupHeight > viewportHeight - padding) {
    top = triggerRect.top - popupHeight - 4;
  }
  
  // If still off top (rare), just position at top with padding
  if (top < padding) {
    top = padding;
  }
  
  // Check horizontal bounds
  if (left < padding) {
    left = padding;
  } else if (left + popupWidth > viewportWidth - padding) {
    left = viewportWidth - popupWidth - padding;
  }
  
  return { top, left };
}

// Gig hover popup component - uses fixed positioning via portal
function GigPopup({ gig, isAdmin, triggerRect }) {
  const totalPayout = gig.assignments?.reduce((sum, a) => sum + parseFloat(a.payout || 0), 0) || 0;
  const margin = gig.clientBilled ? ((gig.clientBilled - totalPayout) / gig.clientBilled * 100).toFixed(1) : 0;
  
  // Calculate position that stays within viewport
  const position = calculatePopupPosition(triggerRect, 280, 350);
  
  return ReactDOM.createPortal(
    <div 
      className="fixed z-[9999] bg-white rounded-lg shadow-xl border border-gray-200 p-3 min-w-[250px] max-w-[300px]"
      style={{ top: position.top, left: position.left }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="font-semibold text-gray-900">{gig.location?.name}</p>
          <p className="text-xs text-gray-500">
            {gig.location?.venue?.name} • {gig.location?.venue?.client?.name}
          </p>
        </div>
        <span className={`${getStatusBadgeClass(gig.status)} text-xs`}>
          {gig.status}
        </span>
      </div>
      
      {/* Details */}
      <div className="space-y-1.5 text-sm">
        <div className="flex items-center gap-2 text-gray-600">
          <Clock className="w-3.5 h-3.5 text-gray-400" />
          <span>{formatTime(gig.startTime)} - {formatTime(gig.endTime)}</span>
        </div>
        
        {gig.address && (
          <div className="flex items-start gap-2 text-gray-600">
            <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5" />
            <span className="text-xs">{gig.address}</span>
          </div>
        )}
        
        {/* Contractors */}
        {gig.assignments?.length > 0 && (
          <div className="pt-2 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-1">Performers</p>
            <div className="space-y-1">
              {gig.assignments.map((a) => (
                <div key={a.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ContractorAvatar 
                      name={a.contractor?.name} 
                      photo={a.contractor?.profilePhoto}
                      size="sm" 
                    />
                    <span className="text-xs text-gray-700">
                      {a.contractor?.displayName || a.contractor?.name}
                      {a.role && <span className="text-gray-400 ml-1">({a.role})</span>}
                    </span>
                  </div>
                  <span className="text-xs font-medium text-gray-600">
                    {formatCurrency(a.payout)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Financials - Admin only */}
        {isAdmin && (
          <div className="pt-2 border-t border-gray-100">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-xs text-gray-500">Billed</p>
                <p className="text-sm font-semibold text-gray-900">{formatCurrency(gig.clientBilled)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Payout</p>
                <p className="text-sm font-semibold text-gray-900">{formatCurrency(totalPayout)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Margin</p>
                <p className="text-sm font-semibold text-green-600">{margin}%</p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Click to edit hint */}
      <div className="mt-2 pt-2 border-t border-gray-100 text-center">
        <span className="text-xs text-gray-400">Click to view/edit</span>
      </div>
    </div>,
    document.body
  );
}

// Individual gig chip with hover
function GigChip({ gig, isAdmin }) {
  const [showPopup, setShowPopup] = useState(false);
  const [triggerRect, setTriggerRect] = useState(null);
  const chipRef = useRef(null);
  const { backgroundColor, textColor, isDraft, isCancelled } = getGigColor(gig);
  
  const handleMouseEnter = () => {
    if (chipRef.current) {
      setTriggerRect(chipRef.current.getBoundingClientRect());
    }
    setShowPopup(true);
  };
  
  // Draft styling: yellow border, no fill
  const draftStyles = isDraft ? {
    border: '2px solid #EAB308',
    backgroundColor: 'white'
  } : {
    backgroundColor
  };
  
  return (
    <div 
      ref={chipRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowPopup(false)}
    >
      <Link
        to={`/admin/gigs/${gig.id}`}
        onClick={(e) => e.stopPropagation()}
        className={`block text-xs p-1 rounded truncate hover:opacity-80 ${textColor} ${isDraft ? 'border-2 border-yellow-500 bg-white' : ''}`}
        style={isDraft ? {} : { backgroundColor }}
      >
        <div className="flex items-center justify-between gap-1">
          <span className="truncate">
            {isDraft && <span className="text-yellow-600 font-medium mr-1">●</span>}
            {isCancelled && <span className="font-bold mr-1">✕</span>}
            {formatTime(gig.startTime)} {gig.location?.name || 'TBD'}
          </span>
          <ContractorAvatars assignments={gig.assignments} max={2} />
        </div>
      </Link>
      
      {showPopup && triggerRect && (
        <GigPopup gig={gig} isAdmin={isAdmin} triggerRect={triggerRect} />
      )}
    </div>
  );
}

function CalendarDay({ date, gigs, currentMonth, onDateClick, isAdmin }) {
  const [expanded, setExpanded] = useState(false);
  
  // FIX: Use date string comparison to avoid UTC timezone issues
  const dayGigs = gigs.filter(g => {
    const gigDateStr = typeof g.date === 'string' ? g.date.split('T')[0] : format(g.date, 'yyyy-MM-dd');
    return isSameDateString(gigDateStr + 'T00:00:00', date);
  });

  const isCurrentMonth = isSameMonth(date, currentMonth);
  const isCurrentDay = isToday(date);
  
  const visibleGigs = expanded ? dayGigs : dayGigs.slice(0, 3);
  const hiddenCount = dayGigs.length - 3;

  const handleCellClick = (e) => {
    // Only trigger new gig if clicking empty space (not a gig chip)
    if (e.target === e.currentTarget || e.target.closest('.date-number')) {
      onDateClick(date);
    }
  };

  return (
    <div 
      className={`min-h-[100px] p-1 border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors
        ${!isCurrentMonth ? 'bg-gray-50' : 'bg-white'}
        ${expanded ? 'row-span-2 z-10' : ''}
      `}
      onClick={handleCellClick}
    >
      <div className={`date-number text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full
        ${isCurrentDay ? 'bg-hub-600 text-white' : ''}
        ${!isCurrentMonth ? 'text-gray-400' : 'text-gray-900'}
      `}>
        {format(date, 'd')}
      </div>
      
      <div className="space-y-1">
        {visibleGigs.map((gig) => (
          <GigChip key={gig.id} gig={gig} isAdmin={isAdmin} />
        ))}
        
        {!expanded && hiddenCount > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(true);
            }}
            className="text-xs text-hub-600 hover:text-hub-700 font-medium pl-1 hover:underline"
          >
            +{hiddenCount} more
          </button>
        )}
        
        {expanded && dayGigs.length > 3 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(false);
            }}
            className="text-xs text-gray-500 hover:text-gray-700 font-medium pl-1 hover:underline"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
}

function CalendarGrid({ currentMonth, gigs, onDateClick, isAdmin }) {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const days = [];
  let day = startDate;

  while (day <= endDate) {
    days.push(day);
    day = addDays(day, 1);
  }

  return (
    <div className="border-t border-l border-gray-200 rounded-lg overflow-hidden">
      {/* Day headers */}
      <div className="grid grid-cols-7 bg-gray-50">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName) => (
          <div 
            key={dayName}
            className="py-2 text-center text-xs font-medium text-gray-500 uppercase border-b border-r border-gray-200"
          >
            {dayName}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {days.map((day, i) => (
          <CalendarDay
            key={i}
            date={day}
            gigs={gigs}
            currentMonth={currentMonth}
            onDateClick={onDateClick}
            isAdmin={isAdmin}
          />
        ))}
      </div>
    </div>
  );
}

function GigListView({ gigs, isAdmin }) {
  if (gigs.length === 0) {
    return (
      <div className="text-center py-12">
        <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No gigs found for this period</p>
      </div>
    );
  }

  // Group by date - FIX: Use date string to avoid UTC issues
  const grouped = gigs.reduce((acc, gig) => {
    const dateKey = typeof gig.date === 'string' ? gig.date.split('T')[0] : format(gig.date, 'yyyy-MM-dd');
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(gig);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([dateKey, dayGigs]) => (
        <div key={dateKey}>
          <h3 className="text-sm font-medium text-gray-500 mb-2">
            {formatDate(dateKey, 'EEEE, MMMM d')}
          </h3>
          <div className="space-y-2">
            {dayGigs.map((gig) => {
              const { backgroundColor, isDraft } = getGigColor(gig);
              const clientColor = gig.location?.venue?.client?.color;
              const totalPayout = gig.assignments?.reduce((sum, a) => sum + parseFloat(a.payout || 0), 0) || 0;
              
              return (
                <Link
                  key={gig.id}
                  to={`/admin/gigs/${gig.id}`}
                  className={`block card hover:shadow-md transition-shadow overflow-hidden ${isDraft ? 'border-2 border-yellow-500 bg-white' : ''} ${gig.status === 'CANCELLED' ? 'bg-red-600' : ''}`}
                >
                  {/* Color bar on left side */}
                  <div className="flex">
                    {clientColor && gig.status !== 'CANCELLED' && (
                      <div 
                        className="w-1.5 flex-shrink-0" 
                        style={{ backgroundColor: clientColor }}
                      />
                    )}
                    <div className={`card-body py-3 flex-1 ${gig.status === 'CANCELLED' ? 'text-white' : ''}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {gig.status === 'CANCELLED' && (
                              <span className="text-white font-bold text-xs">✕ CANCELLED</span>
                            )}
                            <p className={`font-medium ${gig.status === 'CANCELLED' ? 'text-white' : 'text-gray-900'}`}>
                              {gig.location?.venue?.client?.name}
                            </p>
                            {clientColor && gig.status !== 'CANCELLED' && (
                              <span 
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: clientColor }}
                              />
                            )}
                          </div>
                          <p className={`text-sm ${gig.status === 'CANCELLED' ? 'text-red-100' : 'text-gray-600'}`}>
                            {gig.location?.venue?.name} → {gig.location?.name}
                          </p>
                          <p className={`text-sm ${gig.status === 'CANCELLED' ? 'text-red-100' : 'text-gray-500'}`}>
                            {formatTime(gig.startTime)} - {formatTime(gig.endTime)}
                          </p>
                          {/* Contractor names */}
                          {gig.assignments?.length > 0 && (
                            <div className="flex items-center gap-2 mt-1">
                              <ContractorAvatars assignments={gig.assignments} max={4} />
                              <span className={`text-xs truncate ${gig.status === 'CANCELLED' ? 'text-red-100' : 'text-gray-500'}`}>
                                {gig.assignments.map(a => a.contractor?.displayName || a.contractor?.name).filter(Boolean).join(', ')}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <span className={gig.status === 'CANCELLED' ? 'badge bg-red-800 text-white' : getStatusBadgeClass(gig.status)}>
                            {gig.status}
                          </span>
                          {isAdmin && (
                            <>
                              <p className={`text-sm font-medium mt-1 ${gig.status === 'CANCELLED' ? 'text-white' : 'text-gray-900'}`}>
                                {formatCurrency(gig.clientBilled)}
                              </p>
                              <p className={`text-xs ${gig.status === 'CANCELLED' ? 'text-red-100' : 'text-gray-500'}`}>
                                Payout: {formatCurrency(totalPayout)}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminSchedule() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [view, setView] = useState('month'); // month, list
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Check if user is admin or owner (can see financial details)
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OWNER';

  useEffect(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    
    // Add buffer for calendar display
    const fetchStart = startOfWeek(start);
    const fetchEnd = endOfWeek(end);

    setLoading(true);
    api.get(`/gigs?startDate=${fetchStart.toISOString()}&endDate=${fetchEnd.toISOString()}`)
      .then(setGigs)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentMonth]);

  const handleDateClick = (date) => {
    navigate(`/admin/gigs/new?date=${format(date, 'yyyy-MM-dd')}`);
  };

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const goToday = () => setCurrentMonth(new Date());

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Schedule</h1>
        <Link to="/admin/gigs/new" className="btn-primary">
          <Plus className="w-4 h-4" />
          New Gig
        </Link>
      </div>

      {/* Calendar controls */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="btn-ghost p-2">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 min-w-[180px] text-center">
            {format(currentMonth, 'MMMM yyyy')}
          </h2>
          <button onClick={nextMonth} className="btn-ghost p-2">
            <ChevronRight className="w-5 h-5" />
          </button>
          <button onClick={goToday} className="btn-secondary btn-sm ml-2">
            Today
          </button>
        </div>

        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setView('month')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              view === 'month' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'
            }`}
          >
            Month
          </button>
          <button
            onClick={() => setView('list')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              view === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'
            }`}
          >
            List
          </button>
        </div>
      </div>

      {/* Calendar/List view */}
      <div className="card">
        <div className="card-body p-0 lg:p-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
            </div>
          ) : view === 'month' ? (
            <CalendarGrid
              currentMonth={currentMonth}
              gigs={gigs}
              onDateClick={handleDateClick}
              isAdmin={isAdmin}
            />
          ) : (
            <GigListView gigs={gigs} isAdmin={isAdmin} />
          )}
        </div>
      </div>
    </div>
  );
}
